import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import driverRouteService from '../../services/driverRouteService';
import deliveryService from '../../services/deliveryService';
import imageUploadClient from '../../services/imageUploadClient';
import { getCustomerIdFromRoutePoint, getPointDeliveryDetails, isRouteInProgress } from '../../services/routeService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import ConfirmModal from '../shared/ConfirmModal';
import SignatureCaptureModal from './SignatureCaptureModal';
import { ROUTES } from '../../routes';
import { useDriverRoute } from './DriverRouteContext';
import { getPontoById, isPontoPendente } from './roteiroNavigation';
import {
    BackLink,
    CloseButton,
    CommentTextarea,
    FieldValue,
    PageTitle,
    PageWrap,
    PhotoRemoveButton,
    PhotoThumbnailGrid,
    PhotoThumbnailImage,
    PhotoThumbnailItem,
    PrimaryActionButton,
    StatusActionButton,
    StatusPhotoActionButton,
    StatusPhotoActions,
    StatusPanel,
    SectionTitle,
    StatusPanelHeader,
    StatusSelect,
    StatusSummary,
} from './Roteiros.styles';

const STATUS_OPTIONS = [
    { value: '', label: 'Selecione o status' },
    { value: 'SUCCESS', label: 'Sucesso' },
    { value: 'FAILURE', label: 'Falha' },
];

function createPhotoId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function AtualizarStatusPage() {
    const navigate = useNavigate();
    const { pontoId } = useParams();
    const { routeId, roteiro, apiRoute, loading, error, refreshRoute } = useDriverRoute();
    const cameraInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const photosRef = useRef([]);

    const [selectedStatus, setSelectedStatus] = useState('');
    const [comment, setComment] = useState('');
    const [photos, setPhotos] = useState([]);
    const [showStatusSelect, setShowStatusSelect] = useState(false);
    const [showComment, setShowComment] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    const pontoDetailPath = ROUTES.ROTEIROS_PONTO(routeId, pontoId);

    photosRef.current = photos;

    const ponto = useMemo(
        () => (roteiro ? getPontoById(roteiro, pontoId) : null),
        [roteiro, pontoId],
    );

    useEffect(() => {
        return () => {
            photosRef.current.forEach((photo) => {
                URL.revokeObjectURL(photo.previewUrl);
            });
        };
    }, []);

    useEffect(() => {
        if (!roteiro || !ponto) return;

        if (!isRouteInProgress(roteiro.state) || !isPontoPendente(ponto.state)) {
            navigate(pontoDetailPath, { replace: true });
        }
    }, [roteiro, ponto, pontoDetailPath, navigate]);

    const rawPoint = useMemo(() => {
        if (!apiRoute?.points) return null;
        const numericId = Number(pontoId);
        if (!Number.isFinite(numericId)) return null;
        return apiRoute.points.find((point) => point.id === numericId) ?? null;
    }, [apiRoute, pontoId]);

    const customerIdForApi = useMemo(() => {
        const fromRaw = getCustomerIdFromRoutePoint(rawPoint);
        if (fromRaw != null) return fromRaw;
        return ponto?.customerId ?? null;
    }, [rawPoint, ponto?.customerId]);

    const handleConfirm = useCallback(async () => {
        if (customerIdForApi == null) {
            setSubmitError('Não foi possível identificar o cliente da entrega para enviar a atualização.');
            setConfirmOpen(false);
            return;
        }

        setSubmitting(true);
        setSubmitError(null);
        setUploadProgress(null);
        setConfirmOpen(false);

        try {
            let imageIds = [];

            if (photos.length > 0) {
                const deliveryId = ponto?.deliveryId ?? getPointDeliveryDetails(rawPoint)?.id;
                if (!deliveryId) {
                    throw new Error('Não foi possível identificar a entrega vinculada a este ponto.');
                }

                const endpoint = deliveryService.buildDeliveryImagesEndpoint(
                    deliveryId,
                    customerIdForApi,
                );
                const files = photos.map((photo) => photo.file);

                imageIds = await imageUploadClient.uploadAll(endpoint, files, {
                    onProgress: (sent, total) => {
                        setUploadProgress({ sent, total });
                    },
                });

                photos.forEach((photo) => {
                    if (photo.previewUrl) {
                        URL.revokeObjectURL(photo.previewUrl);
                    }
                });
                setPhotos([]);
            }

            await driverRouteService.updatePoint(routeId, pontoId, customerIdForApi, {
                state: selectedStatus,
                images: imageIds,
                comment: comment.trim() || null,
            });

            await refreshRoute();
            navigate(pontoDetailPath, { replace: true });
        } catch (err) {
            setSubmitError(
                getFriendlyErrorMessage(err) || 'Não foi possível atualizar o status deste ponto.',
            );
        } finally {
            setSubmitting(false);
            setUploadProgress(null);
        }
    }, [
        comment,
        customerIdForApi,
        navigate,
        photos,
        ponto?.deliveryId,
        pontoDetailPath,
        pontoId,
        rawPoint,
        refreshRoute,
        routeId,
        selectedStatus,
    ]);

    if (loading) {
        return (
            <PageWrap>
                <PageTitle>Roteiros</PageTitle>
                <FieldValue>Carregando roteiro…</FieldValue>
            </PageWrap>
        );
    }

    if (error || !roteiro || !ponto) {
        return (
            <PageWrap>
                <BackLink type="button" onClick={() => navigate(ROUTES.ROTEIROS)}>
                    ← Voltar aos roteiros
                </BackLink>
                <PageTitle>Roteiros</PageTitle>
                <FieldValue>{error || 'Ponto ou roteiro não encontrado.'}</FieldValue>
            </PageWrap>
        );
    }

    if (!isRouteInProgress(roteiro.state) || !isPontoPendente(ponto.state)) {
        return null;
    }

    const handleCameraClick = () => {
        cameraInputRef.current?.click();
    };

    const handleGalleryClick = () => {
        galleryInputRef.current?.click();
    };

    const appendPhotoFile = (file) => {
        const previewUrl = URL.createObjectURL(file);
        setPhotos((prev) => [
            ...prev,
            {
                id: createPhotoId(),
                name: file.name,
                previewUrl,
                file,
            },
        ]);
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        appendPhotoFile(file);
        event.target.value = '';
    };

    const handleSignatureConfirm = (file) => {
        appendPhotoFile(file);
        setSignatureModalOpen(false);
    };

    const handleRemovePhoto = (photoId) => {
        setPhotos((prev) => {
            const photo = prev.find((item) => item.id === photoId);
            if (photo?.previewUrl) {
                URL.revokeObjectURL(photo.previewUrl);
            }
            return prev.filter((item) => item.id !== photoId);
        });
    };

    const canConfirm = Boolean(selectedStatus) && !submitting;
    const hasSummary = photos.length > 0 || selectedStatus || comment.trim();
    const confirmLabel = (() => {
        if (!submitting) return 'Confirmar';
        if (uploadProgress) return `Enviando fotos (${uploadProgress.sent}/${uploadProgress.total})…`;
        if (photos.length > 0) return 'Preparando…';
        return 'Atualizando…';
    })();

    return (
        <PageWrap>
            <PageTitle>Roteiros</PageTitle>

            <StatusPanel>
                <StatusPanelHeader>
                    <SectionTitle>Ponto {ponto.index}</SectionTitle>
                    <CloseButton
                        type="button"
                        aria-label="Fechar"
                        onClick={() => navigate(pontoDetailPath)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </CloseButton>
                </StatusPanelHeader>

                {submitError ? (
                    <FieldValue style={{ color: '#b91c1c' }}>{submitError}</FieldValue>
                ) : null}

                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                />
                <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                />

                <StatusPhotoActions>
                    <StatusPhotoActionButton onClick={handleCameraClick} disabled={submitting}>
                        Tirar foto
                    </StatusPhotoActionButton>
                    <StatusPhotoActionButton onClick={handleGalleryClick} disabled={submitting}>
                        Galeria
                    </StatusPhotoActionButton>
                    <StatusPhotoActionButton
                        onClick={() => setSignatureModalOpen(true)}
                        disabled={submitting}
                    >
                        Assinatura
                    </StatusPhotoActionButton>
                </StatusPhotoActions>

                {photos.length > 0 && (
                    <PhotoThumbnailGrid>
                        {photos.map((photo) => (
                            <PhotoThumbnailItem key={photo.id}>
                                <PhotoThumbnailImage
                                    src={photo.previewUrl}
                                    alt={photo.name || 'Foto anexada'}
                                />
                                <PhotoRemoveButton
                                    type="button"
                                    aria-label={`Remover foto ${photo.name || ''}`}
                                    disabled={submitting}
                                    onClick={() => handleRemovePhoto(photo.id)}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </PhotoRemoveButton>
                            </PhotoThumbnailItem>
                        ))}
                    </PhotoThumbnailGrid>
                )}

                <StatusActionButton onClick={() => setShowStatusSelect((v) => !v)} disabled={submitting}>
                    Selecionar status
                </StatusActionButton>

                {showStatusSelect && (
                    <StatusSelect
                        value={selectedStatus}
                        disabled={submitting}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </StatusSelect>
                )}

                <StatusActionButton onClick={() => setShowComment((v) => !v)} disabled={submitting}>
                    Comentário
                </StatusActionButton>

                {showComment && (
                    <CommentTextarea
                        value={comment}
                        disabled={submitting}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Digite um comentário sobre o ponto..."
                    />
                )}

                {hasSummary && (
                    <StatusSummary>
                        {photos.length > 0 && (
                            <span>
                                {photos.length} foto{photos.length > 1 ? 's' : ''} anexada{photos.length > 1 ? 's' : ''}
                            </span>
                        )}
                        {selectedStatus && (
                            <span>
                                Status: {STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label}
                            </span>
                        )}
                        {comment.trim() && <span>Comentário: {comment.trim()}</span>}
                    </StatusSummary>
                )}

                <PrimaryActionButton
                    disabled={!canConfirm}
                    onClick={() => setConfirmOpen(true)}
                >
                    {confirmLabel}
                </PrimaryActionButton>
            </StatusPanel>

            <ConfirmModal
                open={confirmOpen}
                title="Confirmar atualização"
                message={
                    'Deseja confirmar a atualização do status deste ponto?\n\nEsta ação não poderá ser desfeita.'
                }
                confirmLabel="Confirmar"
                cancelLabel="Cancelar"
                confirmVariant="primary"
                onConfirm={handleConfirm}
                onCancel={() => setConfirmOpen(false)}
            />

            <SignatureCaptureModal
                open={signatureModalOpen}
                onCancel={() => setSignatureModalOpen(false)}
                onConfirm={handleSignatureConfirm}
            />
        </PageWrap>
    );
}
